import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { Badge } from "../../components/Badge";
import { OrganizationUser, Vendor } from "./type";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { UsersListQueryEventFilter } from "./queries";
import { organizationStore } from "../../store/organization";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { eventStore } from "../../store/event";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { userStore } from "../../store/user";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../queries";
import { Event } from "../../types/event";
import { VENDORS_LIST_QUERY } from "../vendors/queries";
import UserImportCSVModal from "../../components/importCsv/UserImportCSVModal";
import Switch from "react-switch";
import { useGridEdit } from "../helpers/GridEditHelper";
import { useGridSave } from "../helpers/UseGridSave";
import TextSuggestEditor from "../helpers/TextSuggestEditorHelper";
import GridEditActions from "../helpers/GridEditActions";
import { UPDATE_ORGANIZATION_USER_PARTIAL } from "./mutations";
import { RolesListQuery } from "../roles/queries";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getEventStore } = eventStore;
const { getState: getUserStore } = userStore;

type EditableUser = OrganizationUser & { id: number };

const buildSearch = (search: string) => [
  {
    role: {
      name: {
        _ilike: `%${search}%`,
      },
    },
  },
  {
    phone_number: {
      _ilike: `%${search}%`,
    },
  },
  {
    username: {
      _ilike: `%${search}%`,
    },
  },
];

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: EditableUser;
}) => JSX.Element | null;
const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: EditableUser }) => string
): CellRenderer => {
  return (params) => {
    if (params.value === undefined) {
      return null;
    }

    const values = Array.isArray(params.value) ? params.value : [params.value];
    return (
      <>
        {values.map((value, index) => (
          <Badge key={index}>
            {permission ? (
              <Link to={linkFunc({ data: params.data })}>{value}</Link>
            ) : (
              value
            )}
          </Badge>
        ))}
      </>
    );
  };
};

export const usersListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const rawOrganizationId = getState().organizationId;
  const organizationId = parseInt(
    rawOrganizationId !== null ? rawOrganizationId.toString() : "",
    10
  );
  const eventId = getEventStore().eventId;
  const user = getUserStore().user;
  let where: any = {};
  let vendorWhere: any = {};
  let eventWhere: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId && organizationId !== 0) {
    where = {
      ...where,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  if (organizationId && organizationId !== 0) {
    vendorWhere = {
      ...vendorWhere,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  if (organizationId && organizationId !== 0) {
    eventWhere = {
      ...eventWhere,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  if (eventId) {
    eventWhere = {
      ...eventWhere,
      events: {
        _contains: `${eventId}`,
      },
    };
  }
  if (user?.events) {
    where = {
      ...where,
      id: {
        _in: user?.events,
      },
    };
  }
  const rolesWhere: any = {
    is_active: { _eq: true },
    organization_id: { _in: [0, organizationId || 0] },
  };
  console.log("eventID: ", eventId);
  const [
    { data: usersData },
    { data: eventsData },
    { data: vendorsData },
    { data: rolesData },
  ] = await Promise.all([
    client.query({
      query: UsersListQueryEventFilter,
      variables: {
        eventWhere,
      },
    }),
    client.query({
      query: GET_LIST_EVENTS_BY_ORGANIZATION,
      variables: {
        where,
      },
    }),
    client.query({
      query: VENDORS_LIST_QUERY,
      variables: {
        vendorWhere,
      },
    }),
    client.query({
      query: RolesListQuery,
      variables: { where: rolesWhere },
    }),
  ]);
  return {
    users: usersData.users,
    events: eventsData.events,
    vendors: vendorsData.vendors,
    roles: rolesData.roles,
  };
};

export const UsersList = () => {
  const { editCreatePermission } = usePermissions("users");
  const { editCreatePermission: rolesProcessorEditPermission } =
    usePermissions("roles");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const originalActiveRef = useRef<Map<string, boolean>>(new Map());
  const { users, events, vendors, roles } = useLoaderData() as {
    users: EditableUser[];
    events: Event[];
    vendors: Vendor[];
    roles: Array<{ id: number; display_name: string }>;
  };
  const canEdit = !!editCreatePermission;
  const [rowData, setRowData] = useState<EditableUser[]>([]);
  const [isActiveToggle, setIsActiveToggle] = useState(true);
  const {
    pending,
    pendingCount,
    resetBaseline,
    makeEditable,
    setPending,
    getBaselineRows,
  } = useGridEdit<EditableUser>();
  const getRowId = useCallback(
    (p: { data: EditableUser }) => String(p.data.id),
    []
  );
  const gridContext = useMemo(() => ({ pending }), [pending]);
  const gridOptions = useMemo(
    () => ({ enableCellTextSelection: true, context: gridContext }),
    [gridContext]
  );

  const getPatchedValue = (p: any, field?: string) => {
    if (!field) return p.value;
    const id = String(p.data?.id ?? "");
    const patch = p.context?.pending?.[id];
    if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
      return (patch as any)[field];
    }
    return p.data?.[field];
  };

  const defaultColDef = useMemo(
    () => ({
      valueGetter: (p: any) =>
        p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
    }),
    []
  );

  useEffect(() => {
    resetBaseline(users);
    originalActiveRef.current = new Map(
      (users ?? []).map((u) => [String(u.id), !!u.is_active])
    );
    setRowData(users);
  }, [users, resetBaseline]);

  useEffect(() => {
    const api = gridRef.current;
    if (!api) return;
    const model = isActiveToggle
      ? { is_active: { filterType: "text", type: "equals", filter: "Yes" } }
      : null;
    api.setFilterModel(model);
    api.onFilterChanged();
  }, [isActiveToggle, pendingCount]);

  useEffect(() => {
    gridRef.current?.refreshCells({ force: true });
    gridRef.current?.refreshClientSideRowModel("filter");
  }, [pending]);

  const eventMap = events.reduce((map, event) => {
    map[event.id] = event.name;
    return map;
  }, {} as { [key: string]: string });

  const vendorMap = vendors.reduce((map, vendor) => {
    map[vendor.id] = vendor.name;
    return map;
  }, {} as { [key: string]: string });

  const roleList = useMemo(
    () =>
      (roles ?? [])
        .map((r) => ({
          id: Number(r.id),
          label: String(r.display_name ?? "").trim(),
        }))
        .filter((r) => r.label && Number.isFinite(r.id))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [roles]
  );
  const roleIdToLabel = useMemo(
    () => new Map(roleList.map((r) => [r.id, r.label])),
    [roleList]
  );
  const roleLabelToId = useMemo(
    () => new Map(roleList.map((r) => [r.label, r.id])),
    [roleList]
  );

  const generateColDefs = (): ColDef<EditableUser>[] => [
    {
      headerName: "Id",
      valueGetter: (p) => p.data?.id,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/users/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Username",
      field: "username",
      ...makeEditable("username", { type: "text", canEdit }, setRowData),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Is Active?",
      field: "is_active",
      cellDataType: "text",
      sortable: true,
      filter: "agTextColumnFilter",
      ...makeEditable(
        "is_active",
        { type: "boolean", canEdit, toggleOnClick: true },
        setRowData
      ),
      valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),
      filterValueGetter: (p: any) => {
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id] as
          | { is_active?: boolean }
          | undefined;
        if (
          patch &&
          Object.prototype.hasOwnProperty.call(patch, "is_active")
        ) {
          return patch.is_active ? "Yes" : "No";
        }
        return originalActiveRef.current.get(id) ? "Yes" : "No";
      },
    },
    {
      headerName: "First Name",
      field: "first_name",
      ...makeEditable("first_name", { type: "text", canEdit }, setRowData),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Last Name",
      field: "last_name",
      ...makeEditable("last_name", { type: "text", canEdit }, setRowData),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Phone Number",
      field: "phone_number",
      ...makeEditable("phone_number", { type: "text", canEdit }, setRowData),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Organizer",
      valueGetter: (p) => p.data?.organization?.name ?? "",
      cellRenderer: (params: any) => {
        const org = params.data?.organization;
        if (!org) return "";
        return (
          <Badge>
            <Link to={`/organizations/${org.id}/edit`}>{org.name}</Link>
          </Badge>
        );
      },
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Event(s)",
      valueGetter: (p) => {
        const eventIds = p.data?.events || [];
        return eventIds.map((id) => ({
          id,
          name: eventMap[id],
        }));
      },
      cellRenderer: (params: any) => {
        const events = params.value;
        return events.map((event: any, index: any) => (
          <Badge key={index}>
            <Link to={`/events/${event.id}/edit`}>{event.name}</Link>
          </Badge>
        ));
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        (p.data?.events || [])
          .map((id) => eventMap[id as any] ?? "")
          .filter(Boolean)
          .join(", "),
    },
    {
      headerName: "Role",
      field: "role_id",
      cellEditor: TextSuggestEditor,
      cellEditorPopup: true,
      cellEditorParams: (p: { value: any; data: EditableUser }) => ({
        values: roleList.map((r) => r.label),
        showAllOnEdit: true,
        initialValue:
          roleIdToLabel.get(Number(p.value ?? p.data?.role_id)) ?? "",
      }),
      ...makeEditable(
        "role_id" as keyof EditableUser,
        {
          type: "text",
          canEdit,
          parser: (nv: any, ov: any) => {
            const s = String(nv ?? "").trim();
            if (!s) return ov;
            const id = roleLabelToId.get(s);
            return id ?? ov;
          },
          formatter: (v: any) => roleIdToLabel.get(Number(v)) ?? "",
          equals: (a: any, b: any) => Number(a) === Number(b),
        },
        setRowData
      ),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        roleIdToLabel.get(Number(getPatchedValue(p, "role_id"))) ?? "",
    },
    {
      headerName: "Vendor(s)",
      valueGetter: (p) => {
        const vendorIds = p.data?.vendors || [];
        return vendorIds.map((id) => ({
          id,
          name: vendorMap[id],
        }));
      },
      cellRenderer: (params: any) => {
        const vendors = params.value;
        return vendors.map((vendor: any, index: any) => (
          <Badge key={index}>
            <Link to={`/vendors/${vendor.id}/edit`}>{vendor.name}</Link>
          </Badge>
        ));
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        (p.data?.vendors || [])
          .map((id) => vendorMap[id as any] ?? "")
          .filter(Boolean)
          .join(", "),
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.user?.username,
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at
          ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a")
          : "",
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at
          ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a")
          : "",
    },
  ];

  const colDefs: ColDef<EditableUser>[] = useMemo(
    () => generateColDefs(),
    [
      makeEditable,
      setRowData,
      canEdit,
      editCreatePermission,
      rolesProcessorEditPermission,
      eventMap,
      vendorMap,
      roleList,
      roleIdToLabel,
      roleLabelToId,
    ]
  );
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `Users_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };

  const mutateRow = async (id: number, patch: Partial<EditableUser>) => {
    const u = getUserStore().user;
    const data = {
      ...patch,
      last_updated_by: u?.id,
      updated_at: new Date().toISOString(),
    };
    const res = await client.mutate({
      mutation: UPDATE_ORGANIZATION_USER_PARTIAL,
      variables: { id, data },
      errorPolicy: "all",
    });
    if (res.errors?.length)
      throw new Error(res.errors.map((e) => e.message).join("; "));
    if (!res.data?.update_users_by_pk?.id)
      throw new Error(`Update returned no id for user ${id}`);
  };

  const { handleSave } = useGridSave<EditableUser>({
    rowData,
    setRowData,
    pending,
    setPending,
    resetBaseline,
    gridApiRef: gridRef as any,
    revalidate: () => revalidator.revalidate(),
    mutateRow,
    optimistic: true,
    baselines: [{ ref: originalActiveRef, value: (r) => !!r.is_active }],
  });

  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className="grid grid-cols-12 items-center gap-3 min-w-0 py-2">
          <div className="col-span-5 min-w-0">
            <Input
              name="search"
              placeholder="Search Users"
              onChange={(s) => {
                navigate(
                  s && typeof s === "string" && s.length > 0
                    ? `/users/list?search=${s}`
                    : `/users/list`
                );
                revalidator.revalidate();
              }}
            />
          </div>
          <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
            <GridEditActions
              pendingCount={pendingCount}
              pending={pending}
              gridRef={gridRef}
              resetBaseline={resetBaseline}
              baselineData={users}
              isActiveToggle={isActiveToggle}
              setRowData={setRowData}
              setPending={setPending}
              onSave={handleSave}
              getBaselineRows={getBaselineRows}
            />
          </div>
          <div className="col-span-1 flex flex-col items-center">
            <span className="block text-sm font-medium text-gray-500 whitespace-nowrap">
              Active Only
            </span>
            <Switch
              checked={isActiveToggle}
              onChange={() => setIsActiveToggle((prev) => !prev)}
              onColor="#EE2E30"
              onHandleColor="#FFFFFF"
              handleDiameter={18}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
              height={15}
              width={48}
            />
          </div>
          <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
            <SecondaryButton onClick={handleExportCSV}>
              <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
            </SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/users/create`)}>
                Create
              </PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          rowHeight={rowHeight}
          autoSizeStrategy={autoSizeStrategy}
          pagination={true}
          gridOptions={gridOptions}
          paginationPageSize={100}
          getRowId={getRowId}
          onGridReady={(params) => {
            gridRef.current = params.api;
            if (isActiveToggle) {
              params.api.setFilterModel({
                is_active: {
                  filterType: "text",
                  type: "equals",
                  filter: "Yes",
                },
              });
              params.api.onFilterChanged();
            }
          }}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
        />
      </div>
    </div>
  );
};
